import { CheckCircle2, Printer, AlertTriangle, Circle } from "lucide-react";
import { formatDate, formatRelative } from "@/lib/format";

/**
 * Compacte 3-state indicator-rij die per order toont:
 *   ✓ Berekend  |  ⚠ Bewerkt na berekening (alleen bij stale)  |  🖨 Labels geprint (X/Y)
 *
 * De "Bewerkt"-chip toont bij hover de exacte datum/tijd van de laatste
 * wijziging zodat de schuur weet of een herprint nodig is.
 *
 * Props:
 *   - calculated: bool      (heeft een calculation_run)
 *   - stale: bool           (order bewerkt na laatste berekening)
 *   - updated_at: string    (ISO timestamp van laatste wijziging — voor tooltip)
 *   - printed_status: string ("geprint" | "deels-geprint" | "niet-geprint" | "geen-locaties")
 *   - printed_count, total_count: int (voor X/Y display)
 *   - compact: bool         (toont alleen iconen, geen labels — voor recent orders table)
 */
export default function OrderStatusIndicator({
    calculated,
    stale,
    updated_at,
    printed_status,
    printed_count = 0,
    total_count = 0,
    compact = false,
    testid,
}) {
    const updatedLabel = updated_at ? formatDate(updated_at, { withTime: true }) : "";
    const updatedRel = updated_at ? formatRelative(updated_at) : "";

    const items = [
        {
            key: "calculated",
            label: calculated ? "Berekend" : "Niet berekend",
            tone: calculated ? "emerald" : "slate",
            icon: calculated ? CheckCircle2 : Circle,
            title: calculated ? "Karren berekend voor deze order" : "Nog niet berekend",
            testid: `${testid}-calc`,
        },
        {
            key: "stale",
            label: compact ? "" : `Bewerkt · ${updatedRel}`,
            tone: "amber",
            icon: AlertTriangle,
            title: updatedLabel
                ? `Bewerkt na berekening — laatste wijziging op ${updatedLabel}`
                : "Bewerkt na berekening",
            testid: `${testid}-stale`,
            // Alleen tonen als stale=true; anders niet zichtbaar (rustiger UI)
            hidden: !stale,
        },
        {
            key: "printed",
            label: (() => {
                if (printed_status === "geprint") return "Geprint";
                if (printed_status === "deels-geprint") return `${printed_count}/${total_count} geprint`;
                if (printed_status === "geen-locaties") return "—";
                return "Niet geprint";
            })(),
            tone: printed_status === "geprint"
                ? "emerald"
                : printed_status === "deels-geprint"
                    ? "amber"
                    : "slate",
            icon: Printer,
            title: (() => {
                if (printed_status === "geprint") return "Alle karren-labels voor alle locaties zijn geprint";
                if (printed_status === "deels-geprint") return `${printed_count} van ${total_count} locaties geprint`;
                if (printed_status === "geen-locaties") return "Geen locaties op orderregels";
                return "Karren-labels nog niet geprint";
            })(),
            testid: `${testid}-print`,
        },
    ];

    return (
        <div className="flex items-center gap-1.5 flex-wrap" data-testid={testid}>
            {items
                .filter((it) => !it.hidden)
                .map((it) => (
                    <Chip key={it.key} {...it} compact={compact} />
                ))}
        </div>
    );
}

const TONE_CLS = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200/80",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    slate: "bg-ink-50 text-ink-500 border-ink-200",
};

function Chip({ icon: Icon, label, tone, compact, testid, title }) {
    return (
        <span
            data-testid={testid}
            title={title}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[9px] font-semibold uppercase tracking-[0.1em] cursor-default ${TONE_CLS[tone] || TONE_CLS.slate}`}
        >
            <Icon className="h-3 w-3" />
            {!compact && label}
        </span>
    );
}
